// De registratiedatum van één materiaal- of coachadviesregistratie.
//
// WAAROM EEN EIGEN VELD EN NIET `Timestamp`. `Timestamp` is *laatst gewijzigd*:
// elke bewerking van een rij schrijft hem opnieuw. Zolang dat de enige datum is,
// verplaatst een aanpassing in januari een registratie van vorig jaar naar dit
// jaar — inclusief de maatschappelijke waarde die vorig jaar al is gerapporteerd.
// Het kengetal eist dat teller en noemer hetzelfde boekjaar beslaan, dus die
// datum moet vastliggen op het moment van registreren.
//
// ZELFDE PATROON ALS DE MEETDATUM VAN DE PARTICIPATIELADDER: standaard vandaag,
// terug te zetten naar het verleden, nooit naar de toekomst, en vastgeklikt op
// 12:00 lokaal. Dat laatste is geen cosmetica — een avondregistratie op 31
// december is in UTC 1 januari en zou in het verkeerde boekjaar tellen.
//
// ONTBREKEND IS NOOIT VANDAAG. Rijen van vóór deze wijziging hebben geen
// registratiedatum. Die vallen terug op `Timestamp`, maar de bron reist mee zodat
// de UI kan zeggen dat het een benadering is. Stilzwijgend 'vandaag' invullen zou
// elke oude registratie in het lopende boekjaar trekken.
//
// AI-CONTEXT: See docs/plans/ondersteuningsmodule-code-integratie.md (T1)
// AI-CONTEXT: See docs/methodiek-impact-per-euro.md § 1.1

import toMillis from '../dataHelpers/matrixDataHelpers/toMillis'
import { isSentinel } from '../sentinelValue'

/**
 * '' TELT HIER ALS SENTINEL: een leeggemaakt datumveld is geen datum, en de
 * oude lijst had '' er al in staan. Ontbrekend valt terug op `Timestamp` met
 * herkomst `legacy` — nooit stilzwijgend op vandaag (zie de kop).
 *
 * TWEE BEWUSTE VERRUIMINGEN t.o.v. de oude lijst, allebei zonder gevolg voor de
 * uitkomst: de vergelijking is nu hoofdletterongevoelig (dit was de enige plek
 * die op 'NaN' mét hoofdletters toetste terwijl de rest lowercase vergeleek —
 * welke schrijfwijze binnenkomt hangt van de bron af, niet van de plek), en
 * 'none' is toegevoegd. Beide sneuvelden anders één regel later alsnog:
 * `new Date('nan')` en `new Date('none')` zijn ongeldig.
 *
 * De `typeof`-guard blijft staan: hier komen ook Date-objecten, epoch-ms en
 * Firestore-Timestamps langs, en die horen niet door een tekstcontrole.
 * AI-CONTEXT: docs/pitfalls.md#database-url-sentinel-string-persisted
 *
 * @param {*} value
 * @returns {Date|null}
 */
const toDate = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string' && isSentinel(value)) return null

    const ms = toMillis(value)
    if (Number.isFinite(ms)) return new Date(ms)

    const parsed = value instanceof Date ? value : new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * De tijdstempel die bij een gekozen registratiedag hoort: 12:00 lokale tijd,
 * zodat de registratie op die dag blijft staan ongeacht de tijdzone.
 *
 * @param {Date|string|number} dag - Gekozen dag (Date, 'yyyy-mm-dd' of epoch-ms)
 * @returns {Date|null} null bij een onbruikbare datum — nooit een terugval op nu
 */
const registrationTimestampFor = (dag) => {
    const d = toDate(dag)
    if (!d) return null
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0)
}

/**
 * @typedef {object} RegistrationDate
 * @property {number|null} ms - Epoch-ms van de registratie, of null
 * @property {'registered'|'legacy'|'none'} source - Waar de datum vandaan komt
 */

/**
 * De registratiedatum van één entry, met zijn herkomst.
 *
 * `legacy` betekent: deze rij is van vóór het registratiedatum-veld en valt terug
 * op `Timestamp`. Dat is een benadering — de UI moet dat kunnen zeggen in plaats
 * van het als vastgestelde datum te tonen.
 *
 * @param {{RegisteredAt?: *, Timestamp?: *}|null} entry
 * @returns {RegistrationDate}
 */
const readRegistrationDate = (entry) => {
    const registered = toDate(entry?.RegisteredAt)
    if (registered) return { ms: registered.getTime(), source: 'registered' }

    const legacy = toDate(entry?.Timestamp)
    if (legacy) return { ms: legacy.getTime(), source: 'legacy' }

    return { ms: null, source: 'none' }
}

/**
 * Of een gekozen dag ná vandaag ligt. Vergelijkt op dag, niet op tijdstip:
 * registreren om 23:59 vandaag is geen toekomst.
 *
 * Een onbruikbare datum is niet 'toekomstig' — die wordt al geweigerd door
 * `registrationTimestampFor`, en hem hier ook afkeuren zou twee verschillende
 * foutmeldingen voor hetzelfde probleem opleveren.
 *
 * @param {Date|string|number} dag
 * @param {Date} [now]
 * @returns {boolean}
 */
const isFutureDay = (dag, now = new Date()) => {
    const d = toDate(dag)
    if (!d) return false

    const dagStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
    const vandaagStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    return dagStart > vandaagStart
}

export { registrationTimestampFor, readRegistrationDate, isFutureDay }
